'use client';

import {
  LISTING_COUNTRIES,
  getCountryOption,
  getRegionOptions,
} from '@/lib/listing-form';
import type { ListingFormFieldError, ListingFormValues, PinStatus } from '@/lib/listing-form';
import { FieldError, FieldGrid, FieldLabel, TextInput, TextSelect } from './ListingFormFields';
import ListingMapPin from './ListingMapPin';
import { listingFormStyles as s } from './listingFormStyles';

type Props = {
  values: ListingFormValues;
  errors: ListingFormFieldError[];
  disabled?: boolean;
  onFieldChange: <K extends keyof ListingFormValues>(field: K, value: ListingFormValues[K]) => void;
  onAddressFieldChange: (field: 'address' | 'city' | 'state' | 'zip' | 'country', value: string) => void;
  onPinChange: (next: { latitude: number; longitude: number; pinStatus: PinStatus }) => void;
  onPinStatusChange: (status: PinStatus, errorMessage?: string | null) => void;
};

function errFor(errors: ListingFormFieldError[], field: string): string | undefined {
  return errors.find((e) => e.field === field)?.message;
}

export default function StepLocation({
  values,
  errors,
  disabled,
  onAddressFieldChange,
  onPinChange,
  onPinStatusChange,
}: Props) {
  const countryMeta = getCountryOption(values.country);
  const regionOptions = getRegionOptions(values.country);

  return (
    <section style={s.panel}>
      <h2 style={s.sectionTitle}>Property location</h2>
      <p style={s.sectionHint}>
        Enter the full address, confirm the map pin, and adjust it if needed.
      </p>

      <FieldGrid cols={2}>
        <div style={s.field}>
          <FieldLabel htmlFor="listing-country" required>
            Country
          </FieldLabel>
          <TextSelect
            id="listing-country"
            name="country"
            autoComplete="country"
            value={values.country}
            disabled={disabled}
            error={Boolean(errFor(errors, 'country'))}
            onChange={(e) => onAddressFieldChange('country', e.target.value)}
          >
            {LISTING_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </TextSelect>
          <FieldError message={errFor(errors, 'country')} />
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-address" required>
            Street address
          </FieldLabel>
          <TextInput
            id="listing-address"
            name="street-address"
            autoComplete="street-address"
            value={values.address}
            disabled={disabled}
            error={Boolean(errFor(errors, 'address'))}
            onChange={(e) => onAddressFieldChange('address', e.target.value)}
            placeholder="123 Main St"
          />
          <FieldError message={errFor(errors, 'address')} />
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-city" required>
            City
          </FieldLabel>
          <TextInput
            id="listing-city"
            name="address-level2"
            autoComplete="address-level2"
            value={values.city}
            disabled={disabled}
            error={Boolean(errFor(errors, 'city'))}
            onChange={(e) => onAddressFieldChange('city', e.target.value)}
          />
          <FieldError message={errFor(errors, 'city')} />
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-state" required>
            {countryMeta.regionLabel}
          </FieldLabel>
          {regionOptions ? (
            <TextSelect
              id="listing-state"
              name="address-level1"
              autoComplete="address-level1"
              value={values.state}
              disabled={disabled}
              error={Boolean(errFor(errors, 'state'))}
              onChange={(e) => onAddressFieldChange('state', e.target.value)}
            >
              <option value="">Select {countryMeta.regionLabel.toLowerCase()}</option>
              {regionOptions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label} ({r.code})
                </option>
              ))}
            </TextSelect>
          ) : (
            <TextInput
              id="listing-state"
              name="address-level1"
              autoComplete="address-level1"
              value={values.state}
              disabled={disabled}
              error={Boolean(errFor(errors, 'state'))}
              onChange={(e) => onAddressFieldChange('state', e.target.value)}
            />
          )}
          <FieldError message={errFor(errors, 'state')} />
        </div>

        <div style={s.field}>
          <FieldLabel htmlFor="listing-zip" required>
            {countryMeta.postalLabel}
          </FieldLabel>
          <TextInput
            id="listing-zip"
            name="postal-code"
            autoComplete="postal-code"
            value={values.zip}
            disabled={disabled}
            error={Boolean(errFor(errors, 'zip'))}
            onChange={(e) => onAddressFieldChange('zip', e.target.value)}
          />
          <FieldError message={errFor(errors, 'zip')} />
        </div>
      </FieldGrid>

      <div style={{ marginTop: 20 }}>
        <FieldLabel htmlFor="listing-map-pin" required>
          Map pin
        </FieldLabel>
        <ListingMapPin
          address={values.address}
          city={values.city}
          state={values.state}
          zip={values.zip}
          country={values.country}
          latitude={values.latitude}
          longitude={values.longitude}
          pinStatus={values.pinStatus}
          onPinChange={onPinChange}
          onPinStatusChange={onPinStatusChange}
          disabled={disabled}
        />
        <FieldError message={errFor(errors, 'pin')} />
      </div>
    </section>
  );
}
